import { createFileRoute, ErrorComponent } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { fetchFaq } from "@/lib/queries";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { pageMeta, jsonLdScriptTag } from "@/lib/seo";

const faqQueryOptions = queryOptions({
  queryKey: ["faq"],
  queryFn: fetchFaq,
});

export const Route = createFileRoute("/faq")({
  head: () =>
    pageMeta({
      path: "/faq",
      title: "Preguntas frecuentes — Truck Company Center",
      description:
        "Respuestas claras sobre LLC en Florida, EIN, USDOT, MC, Central Dispatch, Super Dispatch, fees y tiempos para car carriers y owner-operators.",
      ogTitle: "FAQ — Truck Company Center",
      ogDescription:
        "Preguntas frecuentes sobre el proceso de lanzamiento de tu trucking company.",
    }),
  loader: async ({ context: { queryClient } }) => {
    await queryClient.ensureQueryData(faqQueryOptions);
  },
  errorComponent: ({ error }) => <ErrorComponent error={error} />,
  component: FaqPage,
});

function FaqPage() {
  const { data: faq } = useSuspenseQuery(faqQueryOptions);
  const jsonLdFaq = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };
  return (
    <div>
      {faq.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: jsonLdScriptTag(jsonLdFaq) }}
        />
      )}
      <section className="border-b border-hairline bg-radial-fade">
        <div className="mx-auto max-w-3xl px-4 py-14 md:px-6 md:py-20">
          <div className="text-xs uppercase tracking-[0.2em] text-label-accent">Dudas</div>
          <h1 className="mt-3 font-display text-4xl font-semibold text-foreground md:text-5xl">Preguntas frecuentes</h1>
          <p className="mt-3 text-muted-foreground">Lo que más nos preguntan, contestado de forma directa.</p>
        </div>
      </section>
      <section className="mx-auto max-w-3xl px-4 py-12 md:px-6 md:py-16">
        <Accordion type="single" collapsible>
          {faq.map((f) => (
            <AccordionItem key={f.id} value={f.id} className="border-hairline">
              <AccordionTrigger className="text-left text-foreground">{f.question}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{f.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </div>
  );
}
